import { useEffect, useState } from "react";
import { UserDefinedList } from "./UserDefinedList";
import { getUserDefinedItems } from "../../../api/userDefinedMaintenance/api";
import { UserDefinedItem, UserDefinedItemType, UserDefinedSortColumn } from "../../../types/domain";
import { MaintenanceSection, MaintenanceSectionContent, MaintenanceSectionHeader } from "../../layout/maintenance";
import { faPlus, faTh, faUserPlus, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@/components/shadcn/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/shadcn/dropdown-menu";
import { useRoles } from "../../../contexts/RolesContext";
import { UserRole } from "../../../types/domain";
import { useLocalStorage } from "../../../hooks";

export type UserDefinedMaintenancePageProps = {
  itemType: UserDefinedItemType;
  entityType: string;
  title: string;
  cacheKey: string;
};

export const UserDefinedMaintenancePage = ({ itemType, entityType, title, cacheKey }: UserDefinedMaintenancePageProps) => {
  const [items, setItems] = useState<UserDefinedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<UserDefinedSortColumn>(UserDefinedSortColumn.Name);
  const [sortAsc, setSortAsc] = useState(true);
  const { hasRole } = useRoles();
  const { updateCacheItem, getCacheItem } = useLocalStorage();

  useEffect(() => {
    const cachedSort = getCacheItem<{ column: UserDefinedSortColumn; asc: boolean }>(cacheKey);
    if (cachedSort) {
      const { column, asc } = cachedSort;
      setSortColumn(column);
      setSortAsc(asc);
      fetchItems(column, asc);
      return;
    }
    fetchItems();
  }, []);

  const fetchItems = async ( sortCol: UserDefinedSortColumn = sortColumn, sortAscVal: boolean = sortAsc ) => {
    try {
      setIsLoading(true);
      setItems(await getUserDefinedItems(itemType, sortCol, sortAscVal));
      setSortColumn(sortCol);
      setSortAsc(sortAscVal);
      updateCacheItem(cacheKey, { column: sortCol, asc: sortAscVal });
    } catch (err) {
      console.error("Error fetching user defined items: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <MaintenanceSection>
        <MaintenanceSectionHeader title={title}>
          {hasRole(UserRole.UserAdmin) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <FontAwesomeIcon icon={faUserPlus} />
                  <span className="hidden sm:inline">Subscribe</span>
                  <FontAwesomeIcon icon={faChevronDown} className="!h-3.5 !w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem 
                  onClick={() => { window.location.href = `MaintenanceEntity.aspx?pageType=UserDefAdmin&pageAction=SubscribeByGroup&entityType=${entityType}`; }}
                >
                  Subscribe by Group/User
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => { window.location.href = `MaintenanceEntity.aspx?pageType=UserDefAdmin&pageAction=SubscribeByEntity&entityType=${entityType}`; }}
                >
                  Subscribe by Category/Item
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {hasRole(UserRole.UserAdmin) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { window.location.href = `Maintenance.aspx?pageType=UserDefCategory&entityType=${entityType}`; }}
            >
              <FontAwesomeIcon icon={faTh} />
              <span className="hidden sm:inline">Categories</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.location.href = `MaintenanceEntity.aspx?pageType=UserDefAdmin&pageAction=New&entityType=${entityType}`;
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:inline">Create Item</span>
          </Button>
        </MaintenanceSectionHeader>
        <MaintenanceSectionContent>
          <UserDefinedList
            items={items}
            fetchItems={fetchItems}
            sortColumn={sortColumn}
            sortAsc={sortAsc}
            isLoading={isLoading}
            entityType={entityType}
          />
        </MaintenanceSectionContent>
      </MaintenanceSection>
    </>
  );
};
